import vp from '../../../apps/json/viewports.json';
import { getBreakpoint } from './_utils';
import _debounce from 'lodash/debounce';
import Flickity from 'flickity';

const accordionClassName = 'Accordion';
const accordionClass = `.${accordionClassName}`;
const itemClassName = `${accordionClassName}-item`;
const itemClass = `.${itemClassName}`;
const regionClass = `${itemClass}-region`;
const collapsedItemClassName = `${itemClassName}--collapsed`;
const buttonClass = `${itemClass}-button`;

const updateFlickityComponents = $container => {
	const $flickityCarousels = $container.find('.flickity-enabled');
	if ($flickityCarousels.length > 0) {
		$flickityCarousels.each((index, flickityCarousel) => {
			const flickity = new Flickity(flickityCarousel);
			flickity.resize();
		});
	}
};

/**
 * Expands an item with an animation and then marks it as not hidden
 * @param  {HTMLElement} item   The item to expand
 * @return {undefined}
 */
function expandItem(item, quick = true) {
	const $item = $(item);
	const $button = $item.find(buttonClass);
	const $region = $item.children(regionClass);
	$region.css('display', 'none');
	$region.css('visibility', '');
	$region.css('padding', '');
	$region.css('height', '');
	$region.slideDown(quick ? 0 : 800);
	$item.removeClass(collapsedItemClassName);
	$button.attr('aria-expanded', 'true');

	updateFlickityComponents($item);
	setTimeout(
		() => {
			$region.removeAttr('hidden');
			$button.trigger('toggle.accordion', ['expand']);
		},
		quick ? 0 : 1000
	);

	const trackInteractions = $button.parents(accordionClass).data('track-interactions');
	const interactionName = $button.parents(accordionClass).data('interaction-name');
	if (trackInteractions) {
		window.FS.Tealium.trackInteraction(
			trackInteractions === true ? 'action' : trackInteractions,
			interactionName || 'policies click',
			$button.find('.Accordion-item-title').text().trim()
		);
	}
}

/**
 * Collapses an item with an animation and then marks it as hidden
 * @param  {HTMLElement} item   The item to collapse
 * @return {undefined}
 */
function collapseItem(item, quick = true) {
	const $item = $(item);
	const $button = $item.find(buttonClass);
	const $region = $item.children(regionClass);

	$region.slideUp({
		duration: quick ? 0 : 800,
		complete: () => {
			$region.attr('hidden', '');
		},
	});
	$item.addClass(collapsedItemClassName);
	$button.attr('aria-expanded', 'false');
	$button.trigger('toggle.accordion', ['collapse']);
}

function toggleButtonDisabled(item, disabled) {
	const $item = $(item);
	const $button = $item.find(buttonClass);
	$button.prop('disabled', disabled);
}

/**
 * Expands an item if closed. Collapses it if open
 * @param  {HTMLElement} item   The item to toggle
 * @return {undefined}
 */
export function toggleItem(item) {
	const $item = $(item);

	$item.hasClass(collapsedItemClassName) ? expandItem($item, false) : collapseItem($item, false);
}

/**
 * Returns true if the accordion is enabled in the current viewport
 * @param  {Array} 	viewportsEnabled  The list of viewports in which the accordion should be enabled
 * @param  {String} viewport   				The optional viewport to check
 * @return {Boolean}									True if the accordion should be enabled in this viewport. False otherwise
 */
function isEnabled(viewportsEnabled, viewport = getBreakpoint()) {
	return viewportsEnabled.indexOf(viewport) >= 0;
}

/**
 * Initiates one accordion: Collapses all items and adds events
 * @param  {Element} element   The HTML Element for the list of items
 * @return {undefined}
 */
function initAccordion(element) {
	const $element = $(element);
	const $items = $element.children(itemClass);
	const viewportsEnabled = $element.data('viewports')
		? $element.data('viewports').split(',')
		: vp.viewports;
	let previousBreakpoint = getBreakpoint();

	$items.each((index, item) => {
		const $item = $(item);
		const itemViewports = $item.data('viewports')
			? $item.data('viewports').split(',')
			: viewportsEnabled;
		const startExpanded = $item.data('startExpanded');
		if (isEnabled(itemViewports)) {
			if (!startExpanded) {
				collapseItem(item);
			}
			toggleButtonDisabled(item, false);
		} else {
			toggleButtonDisabled(item, true);
		}

		// Add events
		const $button = $item.find(buttonClass);
		$button.on(
			'click.accordion',
			_debounce(() => isEnabled(viewportsEnabled) && toggleItem(item), 500, {
				trailing: true,
				leading: true,
			})
		);
		$button.on('expand.accordion', () => {
			if (isEnabled(viewportsEnabled)) {
				expandItem($item, false);
			}
		});
		$($button).on('keyup.accordion', event => {
			if (isEnabled(viewportsEnabled)) {
				// Up arrow
				if (event.which === 38) {
					const $prev = index === 0 ? $item.siblings(':last') : $item.prev();
					$prev.find(buttonClass).focus();
				}

				// Down arrow
				if (event.which === 40) {
					const $next =
						index === $items.length - 1 ? $item.siblings(':first') : $item.next();
					$next.find(buttonClass).focus();
				}
			}
		});
	});

	$(window).on(
		'resize.accordion',
		_debounce(() => {
			const currentBreakpoint = getBreakpoint();
			if (currentBreakpoint !== previousBreakpoint) {
				$items.each((index, item) => {
					const $item = $(item);
					const itemViewports = $item.data('viewports')
						? $item.data('viewports').split(',')
						: viewportsEnabled;
					const previouslyEnabled = isEnabled(itemViewports, previousBreakpoint);
					const currentlyEnabled = isEnabled(itemViewports);

					if (previouslyEnabled !== currentlyEnabled) {
						toggleButtonDisabled(item, !currentlyEnabled);
						currentlyEnabled ? collapseItem(item) : expandItem(item);
					}
				});
				previousBreakpoint = currentBreakpoint;
			}
		}, 200)
	);

	// Needed because it should run after the first collapse
	setTimeout(() => $element.trigger('startup.accordion'), 1200);
}

/**
 * Initiates all the accordions on the page
 * @return {undefined}
 */
export default function initAccordions() {
	$(accordionClass).each((index, element) => initAccordion(element));
}
